import type { ReactNode } from 'react';
import type { JobPlan } from '@/services/api/mm-program-jobs';
import { pluralize } from '@/utils/string';
import { describeJobTarget } from './job-request';

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid grid-cols-[9rem_1fr] gap-3 text-sm">
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="text-foreground">{children}</dd>
  </div>
);
Row.displayName = 'Row';

const describeMonthly = (monthly: NonNullable<JobPlan['monthly']>) => {
  const to = monthly.to === 'monthly recipients' ? "One Trading's monthly recipients" : monthly.to;
  const bcc = monthly.bcc === null ? '' : monthly.bcc.length ? `, bcc ${monthly.bcc.join(', ')}` : ', no bcc';
  return `${to}${bcc}`;
};

/** What a job will do, from bop-api's dry run. */
export const JobPlanSummary = ({ plan }: { plan: JobPlan }) => {
  if (plan.type === 'score' && !plan.reportDays.length && !plan.reportMonths.length) {
    return (
      <dl className="flex flex-col gap-2 pt-1">
        <Row label="Runs">{describeJobTarget(plan)}</Row>
        <Row label="Reports">None sent; the job only rescores</Row>
      </dl>
    );
  }
  return <ReportPlan plan={plan} />;
};
JobPlanSummary.displayName = 'JobPlanSummary';

const ReportPlan = ({ plan }: { plan: JobPlan }) => (
  <dl className="flex flex-col gap-2 pt-1">
    {plan.runs.length ? <Row label="Runs">{describeJobTarget(plan)}</Row> : null}
    <Row label="Daily reports">{plan.reportDays.length ? plan.reportDays.join(', ') : 'None'}</Row>
    {plan.partialDays.length ? (
      <Row label="Partly rescored">
        <span className="text-amber-500">{plan.partialDays.join(', ')}: these reports use the day's other runs as they were scored before.</span>
      </Row>
    ) : null}
    {plan.reportMonths.length ? <Row label="Monthly reports">{plan.reportMonths.join(', ')}</Row> : null}
    <Row label="Daily emails">
      {plan.dailyRecipients.length ? (
        <ul className="flex flex-col gap-0.5">
          {plan.dailyRecipients.map((recipient) => (
            <li key={`${recipient.name}-${recipient.to}`}>
              {recipient.name} → {recipient.to}
              {recipient.bcc.length ? <span className="text-muted-foreground"> (bcc {recipient.bcc.join(', ')})</span> : null}
            </li>
          ))}
        </ul>
      ) : (
        'None sent'
      )}
    </Row>
    {plan.reportMonths.length ? <Row label="Monthly email">{plan.monthly ? describeMonthly(plan.monthly) : 'Not sent'}</Row> : null}
    {plan.emailsMarketMakers ? (
      <Row label="Already sent">
        {plan.resend
          ? 'Sent again to market makers who already received the day.'
          : `Skipped for market makers who already received the day. ${pluralize(plan.dailyRecipients.length, 'market maker')} at most.`}
      </Row>
    ) : null}
  </dl>
);
ReportPlan.displayName = 'ReportPlan';
